import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import AICoachInsightCard from '../components/AICoachInsightCard';
import ConsistencyCard from '../components/ConsistencyCard';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { 
  Droplets, 
  Calculator, 
  Bell, 
  Plus, 
  Minus,
  GlassWater,
  Target,
  Calendar,
  TrendingUp,
  Award,
  Loader2,
  RefreshCw,
  Sun,
  Moon,
  Volume2,
  VolumeX,
  ChevronRight,
  CheckCircle2,
  Info,
  Edit3,
  BarChart3,
  Trophy,
  Frown,
  Smile,
  Meh,
  Mail,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import { play as playSound } from '../lib/soundDirector';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const Hydration = () => {
  const { user, updateUser } = useAuth();
  const { language, t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(false);
  
  // Today's data
  const [todayData, setTodayData] = useState({
    total_ml: 0,
    target_ml: 2000,
    percentage: 0,
    logs: []
  });
  
  // History
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({});
  // Iter. 406.fr.46 — Consistency stats (score % + streaks)
  const [consistency, setConsistency] = useState(null);
  // Iter. 406.fr.45 — Period toggle for chart (week / month / year)
  const [hydrPeriod, setHydrPeriod] = useState('week');
  
  // Recommendation
  const [recommendation, setRecommendation] = useState(null);
  
  // Calculator state
  const [showCalculator, setShowCalculator] = useState(false);
  const [calcData, setCalcData] = useState({
    weight_kg: user?.current_weight_lbs ? (user.current_weight_lbs / 2.20462).toFixed(0) : '',
    age: user?.age || '',
    sex: user?.sex || 'male',
    activity_level: user?.activity_level || 'moderate',
    climate: 'temperate'
  });
  const [calculating, setCalculating] = useState(false);
  
  // Reminder settings
  const [showReminders, setShowReminders] = useState(false);
  const [reminderSettings, setReminderSettings] = useState({
    enabled: false,
    start_hour: 7,
    end_hour: 22,
    interval_minutes: 60,
    sound_enabled: true
  });
  const [savingReminders, setSavingReminders] = useState(false);
  
  // Weekly summary
  const [showWeeklySummary, setShowWeeklySummary] = useState(false);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [loadingWeekly, setLoadingWeekly] = useState(false);

  // Phase 2 retroactive (2026-04-29) — backfill a missed day
  const [showBackfill, setShowBackfill] = useState(false);
  const [backfillDate, setBackfillDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  });
  const [backfillMl, setBackfillMl] = useState('');
  const [backfillSaving, setBackfillSaving] = useState(false);
  const backfillMin = (() => { const d = new Date(); d.setDate(d.getDate() - 30); const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${y}-${m}-${dd}`; })();
  const backfillMax = (() => { const d = new Date(); d.setDate(d.getDate() - 1); const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${y}-${m}-${dd}`; })();

  const submitBackfill = async () => {
    const ml = parseInt(backfillMl, 10);
    if (!ml || ml < 100 || ml > 8000) {
      toast.error(t('hydration.quantite_invalide_100_8000_ml', 'Quantité invalide (100-8000 ml)'));
      return;
    }
    setBackfillSaving(true);
    try {
      await api.post('/hydration/log', { amount_ml: ml, target_date: backfillDate });
      toast.success(t('hydration.saved_for_date', 'Hydratation enregistrée pour le {{date}}', { date: backfillDate }));
      setShowBackfill(false);
      setBackfillMl('');
      // Refresh weekly history if currently showing
      try { fetchData(); } catch { /* fetchData defined elsewhere */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setBackfillSaving(false);
    }
  };
  
  // Custom target
  const [showTargetEditor, setShowTargetEditor] = useState(false);
  const [customTarget, setCustomTarget] = useState(2000);
  const [savingTarget, setSavingTarget] = useState(false);
  
  // Email report
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Quick add amounts
  const quickAmounts = [100, 250, 330, 500];
  const [customAmount, setCustomAmount] = useState(250);

  // Assistant IA deep-link pre-fill (e.g. /hydration?amount=500)
  const [searchParams, setSearchParams] = useSearchParams();
  const [aiPrefillAmount, setAiPrefillAmount] = useState(null);

  useEffect(() => {
    const a = searchParams.get('amount');
    if (a) {
      const n = parseInt(a, 10);
      if (!isNaN(n) && n > 0 && n <= 2000) {
        setAiPrefillAmount(n);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('amount');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmAiPrefill = async () => {
    if (!aiPrefillAmount) return;
    const amt = aiPrefillAmount;
    setAiPrefillAmount(null);
    await logWater(amt);
    toast.success(t('hydration.ml_logged', '{{amt}} ml loggé !', { amt }));
  };
  
  // Water reminder timer
  const reminderTimerRef = useRef(null);
  
  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const daysForPeriod = hydrPeriod === 'week' ? 7 : hydrPeriod === 'month' ? 30 : 365;
      const [todayRes, historyRes, recRes, remindersRes, targetRes] = await Promise.all([
        api.get('/hydration/today'),
        api.get(`/hydration/history?days=${daysForPeriod}`),
        api.get('/hydration/recommendation'),
        api.get('/hydration/reminders'),
        api.get('/hydration/target')
      ]);
      
      setTodayData(todayRes.data);
      setHistory(historyRes.data.history);
      setStats(historyRes.data.stats);
      setConsistency(historyRes.data.consistency || null);
      
      if (recRes.data.recommendation) {
        setRecommendation(recRes.data.recommendation);
      }
      
      setReminderSettings(remindersRes.data.settings);
      setCustomTarget(targetRes.data.target_ml);
    } catch (error) {
      console.error('Error fetching hydration data:', error);
    } finally {
      setLoading(false);
    }
  }, [hydrPeriod]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Iter. 406.fr.45 — Bucket history for the chart per period.
  // week: raw 7 daily rows • month: raw 30 rows • year: 12 monthly averages.
  const hydrChartData = useMemo(() => {
    const rows = history || [];
    if (hydrPeriod !== 'year') return rows;
    // Aggregate by YYYY-MM
    const buckets = new Map();
    for (const r of rows) {
      const ym = (r.date || '').slice(0, 7);
      if (!ym) continue;
      if (!buckets.has(ym)) buckets.set(ym, { ym, total_ml: 0, days: 0, target_ml: r.target_ml || 0 });
      const b = buckets.get(ym);
      b.total_ml += Number(r.total_ml) || 0;
      b.days += 1;
      if (r.target_ml) b.target_ml = r.target_ml;
    }
    const sorted = [...buckets.values()].sort((a, b) => a.ym.localeCompare(b.ym));
    return sorted.map((b) => {
      const [y, m] = b.ym.split('-').map(Number);
      const monthShort = new Date(y, m - 1, 1).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short' });
      return {
        date: monthShort,
        year_month: b.ym,
        total_ml: Math.round(b.total_ml / b.days),
        target_ml: b.target_ml,
        days: b.days,
        goal_reached: (b.total_ml / b.days) >= b.target_ml,
      };
    });
  }, [history, hydrPeriod, language]);
  
  // Setup reminder notifications
  useEffect(() => {
    if (!reminderSettings.enabled) {
      if (reminderTimerRef.current) {
        clearInterval(reminderTimerRef.current);
      }
      return;
    }
    
    const checkAndNotify = () => {
      const now = new Date();
      const hour = now.getHours();
      
      if (hour >= reminderSettings.start_hour && hour < reminderSettings.end_hour) {
        // Check if we should show notification
        // iOS Safari (non-PWA) exposes `Notification` in window but throws on `new Notification()`.
        // We must (1) feature-detect, (2) check permission, and (3) wrap in try/catch.
        // Prefer ServiceWorker.showNotification() when available (works on iOS PWAs).
        if (typeof window === 'undefined' || !('Notification' in window)) return;
        if (Notification.permission !== 'granted') return;

        const title = t('hydration.n_oubliez_pas_de_boire', 'N\'oubliez pas de boire!');
        const options = {
          body: t('hydration.notif_body_status', "Vous avez bu {{drunk}}ml sur {{target}}ml aujourd'hui", { drunk: todayData.total_ml, target: todayData.target_ml }),
          icon: '/icons/icon-192x192.png',
          tag: 'water-reminder'
        };

        try {
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            // Use ServiceWorker registration if available (required for iOS PWA notifications)
            navigator.serviceWorker.ready
              .then((reg) => reg.showNotification(title, options))
              .catch(() => {
                // Last-resort fallback to constructor; ignore if it throws.
                try { new Notification(title, options); } catch (_) { /* iOS Safari non-PWA */ }
              });
          } else {
            new Notification(title, options);
          }
        } catch (_) {
          // iOS Safari (non-PWA) throws "Failed to construct 'Notification'". Silently ignore.
        }
      }
    };
    
    // Set interval based on settings
    reminderTimerRef.current = setInterval(checkAndNotify, reminderSettings.interval_minutes * 60 * 1000);
    
    return () => {
      if (reminderTimerRef.current) {
        clearInterval(reminderTimerRef.current);
      }
    };
  }, [reminderSettings, todayData, language]);
  
  // Log water intake — accepts rapid-fire taps (no client-side click guard).
  // We compute the optimistic total BEFORE the async call so the UI updates instantly,
  // and reconcile with the server's total on response.
  const logWater = async (amount) => {
    const optimisticTotal = (todayData.total_ml || 0) + amount;
    const optimisticPct = todayData.target_ml > 0
      ? Math.min(100, Math.round((optimisticTotal / todayData.target_ml) * 100))
      : 0;
    setTodayData(prev => ({
      ...prev,
      total_ml: optimisticTotal,
      percentage: optimisticPct,
      logs: [...prev.logs, { amount_ml: amount, timestamp: new Date().toISOString() }],
    }));
    setLogging(true);
    try {
      const response = await api.post('/hydration/log', { amount_ml: amount });
      setTodayData(prev => ({
        ...prev,
        total_ml: response.data.today_total_ml,
        percentage: response.data.percentage,
      }));

      // Show success feedback
      if (response.data.percentage >= 100 && todayData.percentage < 100) {
        toast.success(t('hydration.objectif_atteint_bravo', 'Objectif atteint! Bravo!'));
        try { playSound('deficit_hit'); } catch (_) {}
      } else {
        toast.success(`+${amount}ml`);
        try { playSound('water_glass'); } catch (_) {}
      }
    } catch (error) {
      // Roll back optimistic update
      setTodayData(prev => ({
        ...prev,
        total_ml: Math.max(0, (prev.total_ml || 0) - amount),
        logs: prev.logs.slice(0, -1),
      }));
      toast.error(t('hydration.erreur_lors_de_l_enregistrement', 'Erreur lors de l\'enregistrement'));
    } finally {
      setLogging(false);
    }
  };
  
  // Calculate water needs
  const calculateWaterNeeds = async () => {
    if (!calcData.weight_kg || !calcData.age) {
      toast.error(t('hydration.veuillez_remplir_tous_les_champs', 'Veuillez remplir tous les champs'));
      return;
    }
    
    setCalculating(true);
    try {
      const response = await api.post('/hydration/calculate', {
        weight_kg: parseFloat(calcData.weight_kg),
        age: parseInt(calcData.age),
        sex: calcData.sex,
        activity_level: calcData.activity_level,
        climate: calcData.climate
      });
      
      setRecommendation(response.data.recommendation);
      setTodayData(prev => ({
        ...prev,
        target_ml: response.data.recommendation.daily_ml
      }));
      
      toast.success(t('hydration.objectif_calcule', 'Objectif calculé!'));
      setShowCalculator(false);
      
    } catch (error) {
      toast.error(t('hydration.erreur_de_calcul', 'Erreur de calcul'));
    } finally {
      setCalculating(false);
    }
  };
  
  // Save reminder settings
  const saveReminderSettings = async () => {
    setSavingReminders(true);
    try {
      await api.post('/hydration/reminders', reminderSettings);
      
      // Request notification permission if enabling
      if (reminderSettings.enabled && 'Notification' in window) {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          toast.info(t('hydration.autorisez_les_notifications_pour_recevoi', 'Autorisez les notifications pour recevoir les rappels'));
        }
      }
      
      toast.success(t('hydration.rappels_enregistres', 'Rappels enregistrés'));
      setShowReminders(false);
    } catch (error) {
      toast.error(t('hydration.erreur', 'Erreur'));
    } finally {
      setSavingReminders(false);
    }
  };
  
  // Save custom target
  const saveCustomTarget = async () => {
    if (customTarget < 1000 || customTarget > 6000) {
      toast.error(t('hydration.l_objectif_doit_etre_entre_1l_et_6l', 'L\\\'objectif doit être entre 1L et 6L'));
      return;
    }
    
    setSavingTarget(true);
    try {
      const response = await api.post('/hydration/target', { target_ml: customTarget });
      
      setTodayData(prev => ({
        ...prev,
        target_ml: customTarget,
        percentage: prev.total_ml > 0 ? round((prev.total_ml / customTarget) * 100, 1) : 0
      }));
      
      toast.success(response.data.message);
      setShowTargetEditor(false);
      fetchData(); // Refresh all data
    } catch (error) {
      toast.error(error.response?.data?.detail || (t('hydration.erreur', 'Erreur')));
    } finally {
      setSavingTarget(false);
    }
  };
  
  // Load weekly summary
  const loadWeeklySummary = async () => {
    setLoadingWeekly(true);
    setShowWeeklySummary(true);
    try {
      const response = await api.get('/hydration/weekly-summary');
      setWeeklySummary(response.data);
    } catch (error) {
      toast.error(t('hydration.erreur_de_chargement', 'Erreur de chargement'));
      setShowWeeklySummary(false);
    } finally {
      setLoadingWeekly(false);
    }
  };
  
  // Helper function
  const round = (num, decimals) => Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  
  // Send email report
  const sendEmailReport = async () => {
    setSendingEmail(true);
    try {
      const response = await api.post('/email/send-hydration-report');
      toast.success(response.data.message || (t('hydration.rapport_envoye', 'Rapport envoyé!')));
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (t('hydration.erreur_d_envoi', 'Erreur d\\\'envoi'));
      toast.error(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };
  
  // Get progress color
  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 75) return 'bg-blue-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-orange-500';
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="space-y-6 pb-20">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/20 border-2 border-blue-500 rounded-xl mb-4">
            <Droplets className="w-8 h-8 text-blue-500" />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-foreground">
            {t('hydration.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('hydration.subtitle')}
          </p>
          {/* Phase 2 retroactive — discreet "Rattraper une journée" link */}
          <button
            type="button"
            onClick={() => setShowBackfill(true)}
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-orange-400 hover:text-orange-300 transition-colors"
            data-testid="hydration-backfill-cta"
          >
            <Calendar className="w-3 h-3" />
            {t('hydration.rattraper_une_journee', 'Rattraper une journée')}
          </button>
        </div>

        {/* Iter. 406.fr.43 — Lecture Coach YUMIX (lazy, non-bloquant) */}
        <AICoachInsightCard endpoint="/hydration/ai-summary" period="week" />

        {/* Phase 2 — Backfill dialog */}
        {showBackfill && (
          <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
            <Card className="w-full max-w-sm" data-testid="hydration-backfill-dialog">
              <CardHeader>
                <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-400" />
                  {t('hydration.rattraper_une_journee', 'Rattraper une journée')}
                </CardTitle>
                <CardDescription className="text-xs">
                  {t('hydration.ajoute_le_total_d_eau_bu_pour_une_journe', 'Ajoute le total d\\\'eau bu pour une journée passée (jusqu\\\'à 30j).')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                    {t('hydration.date', 'Date')}
                  </Label>
                  <Input
                    type="date"
                    value={backfillDate}
                    min={backfillMin}
                    max={backfillMax}
                    onChange={(e) => setBackfillDate(e.target.value || backfillMax)}
                    disabled={backfillSaving}
                    className="h-9 text-sm mt-1"
                    data-testid="hydration-backfill-date"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                    {t('hydration.quantite_bue_ml', 'Quantité bue (ml)')}
                  </Label>
                  <Input
                    type="number"
                    placeholder="2000"
                    value={backfillMl}
                    onChange={(e) => setBackfillMl(e.target.value)}
                    disabled={backfillSaving}
                    min={100}
                    max={8000}
                    className="h-9 text-sm mt-1"
                    data-testid="hydration-backfill-amount"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    onClick={() => { setShowBackfill(false); setBackfillMl(''); }}
                    disabled={backfillSaving}
                    className="flex-1"
                    data-testid="hydration-backfill-cancel"
                  >
                    {t('hydration.annuler', 'Annuler')}
                  </Button>
                  <Button
                    onClick={submitBackfill}
                    disabled={backfillSaving || !backfillMl}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-black font-bold uppercase tracking-wider"
                    data-testid="hydration-backfill-submit"
                  >
                    {backfillSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (t('hydration.enregistrer', 'Enregistrer'))}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Assistant IA pre-fill banner */}
        {aiPrefillAmount && (
          <Card className="border-primary ring-2 ring-primary/40 bg-primary/5 animate-pulse-once" data-testid="hydration-ai-prefill-banner">
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-1">
                  {t('hydration.assistant_ia', 'Assistant IA')}
                </p>
                <p className="text-sm text-foreground">
                  {t('hydration.ai_prefill_question', "Logger {{amt}} ml d'eau ?", { amt: aiPrefillAmount })}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setAiPrefillAmount(null)}
                  className="h-9 text-xs text-muted-foreground"
                  data-testid="hydration-ai-dismiss"
                >
                  {t('hydration.annuler', 'Annuler')}
                </Button>
                <Button
                  size="sm"
                  onClick={confirmAiPrefill}
                  className="h-9 px-4 text-xs font-bold bg-primary text-primary-foreground"
                  data-testid="hydration-ai-confirm"
                >
                  {t('hydration.log_ml_amount', 'Logger {{amt}} ml', { amt: aiPrefillAmount })}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Today's Progress Card */}
        <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/10 border-blue-500/30">
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              {/* Water Drop Animation */}
              <div className="relative w-32 h-32 mx-auto mb-4">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  {/* Background drop */}
                  <path
                    d="M50 5 C50 5, 15 45, 15 65 C15 85, 30 95, 50 95 C70 95, 85 85, 85 65 C85 45, 50 5, 50 5 Z"
                    fill="none"
                    stroke="rgba(59, 130, 246, 0.3)"
                    strokeWidth="3"
                  />
                  {/* Filled portion */}
                  <defs>
                    <clipPath id="dropClip">
                      <path d="M50 5 C50 5, 15 45, 15 65 C15 85, 30 95, 50 95 C70 95, 85 85, 85 65 C85 45, 50 5, 50 5 Z" />
                    </clipPath>
                  </defs>
                  <rect
                    x="0"
                    y={100 - Math.min(todayData.percentage, 100)}
                    width="100"
                    height={Math.min(todayData.percentage, 100)}
                    fill="url(#waterGradient)"
                    clipPath="url(#dropClip)"
                    className="transition-all duration-500"
                  />
                  <defs>
                    <linearGradient id="waterGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="100%" stopColor="#06b6d4" />
                    </linearGradient>
                  </defs>
                </svg>
                {/* Percentage text */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-black text-white drop-shadow-lg">
                    {Math.round(todayData.percentage)}%
                  </span>
                </div>
              </div>
              
              <p className="text-3xl font-black text-foreground">
                {todayData.total_ml} <span className="text-lg font-normal text-muted-foreground">/ {todayData.target_ml} ml</span>
              </p>
              <p className="text-sm text-muted-foreground">
                {Math.round(todayData.total_ml / 250)} {t('hydration.verres_bus', 'verres bus')} 
                {todayData.target_ml > todayData.total_ml && (
                  <> • {Math.ceil((todayData.target_ml - todayData.total_ml) / 250)} {t('hydration.restants', 'restants')}</>
                )}
              </p>
            </div>
            
            {/* Progress bar */}
            <div className="relative h-4 bg-secondary/50 rounded-full overflow-hidden mb-4">
              <div 
                className={`h-full transition-all duration-500 ${getProgressColor(todayData.percentage)}`}
                style={{ width: `${Math.min(todayData.percentage, 100)}%` }}
              />
            </div>
            
            {todayData.percentage >= 100 && (
              <div className="flex items-center justify-center gap-2 text-green-500 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                {t('hydration.objectif_atteint', 'Objectif atteint!')}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Quick Add Buttons */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              {t('hydration.add_water')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quick buttons */}
            <div className="grid grid-cols-4 gap-2">
              {quickAmounts.map(amount => (
                <Button
                  key={amount}
                  variant="outline"
                  className="h-16 flex flex-col gap-1"
                  onClick={() => logWater(amount)}
                >
                  <GlassWater className="w-5 h-5 text-blue-500" />
                  <span className="text-xs">{amount}ml</span>
                </Button>
              ))}
            </div>
            
            {/* Custom amount */}
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCustomAmount(Math.max(50, customAmount - 50))}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(parseInt(e.target.value) || 0)}
                  className="text-center"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCustomAmount(customAmount + 50)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <Button
                onClick={() => logWater(customAmount)}
                disabled={customAmount <= 0}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {logging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                {t('hydration.ajouter', 'Ajouter')}
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className="h-14 flex items-center gap-2"
            onClick={() => setShowTargetEditor(true)}
          >
            <Edit3 className="w-5 h-5 text-primary" />
            <span className="text-sm">{t('hydration.objectif', 'Objectif')}</span>
            <span className="text-xs text-muted-foreground">{(todayData.target_ml / 1000).toFixed(1)}L</span>
          </Button>
          <Button
            variant="outline"
            className="h-14 flex items-center gap-2"
            onClick={loadWeeklySummary}
          >
            <BarChart3 className="w-5 h-5 text-primary" />
            <span className="text-sm">{t('hydration.resume', 'Résumé')}</span>
          </Button>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className="h-14 flex items-center gap-2"
            onClick={() => setShowCalculator(true)}
          >
            <Calculator className="w-5 h-5 text-primary" />
            <span className="text-sm">{t('hydration.calculateur', 'Calculateur')}</span>
          </Button>
          <Button
            variant="outline"
            className="h-14 flex items-center gap-2"
            onClick={() => setShowReminders(true)}
          >
            <Bell className="w-5 h-5 text-primary" />
            <span className="text-sm">{t('hydration.rappels', 'Rappels')}</span>
            {reminderSettings.enabled && (
              <span className="w-2 h-2 bg-green-500 rounded-full" />
            )}
          </Button>
        </div>
        
        {/* Iter. 406.fr.46 — Hydration Consistency Card (Strava-lite KPI) */}
        {consistency && consistency.days_tracked > 0 && (
          <ConsistencyCard
            consistency={consistency}
            theme="blue"
            title={language === 'fr' ? 'Consistance Hydratation' : 'Hydration Consistency'}
            unitLabel={language === 'fr' ? 'jours à l\'objectif' : 'days on target'}
            streakLabel={language === 'fr' ? 'Série actuelle' : 'Current streak'}
            bestLabel={language === 'fr' ? 'Meilleure série' : 'Best streak'}
            onTargetLabel={
              language === 'fr'
                ? `Sur les ${hydrPeriod === 'week' ? '7' : hydrPeriod === 'month' ? '30' : '365'} derniers jours.`
                : `Over the last ${hydrPeriod === 'week' ? '7' : hydrPeriod === 'month' ? '30' : '365'} days.`
            }
            fr={language === 'fr'}
            testId="hydration-consistency-card"
          />
        )}

        {/* Weekly History Chart */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                {t('hydration.historique', 'Historique')}
              </CardTitle>
              {/* Iter. 406.fr.45 — Period toggle (Week / Month / Year) */}
              <div
                className="flex items-center gap-0.5 bg-secondary/50 rounded-lg p-0.5"
                data-testid="hydration-period-toggle"
              >
                {[
                  { key: 'week',  fr: 'Semaine', en: 'Week' },
                  { key: 'month', fr: 'Mois',    en: 'Month' },
                  { key: 'year',  fr: 'Année',   en: 'Year' },
                ].map(p => (
                  <button
                    key={p.key}
                    onClick={() => setHydrPeriod(p.key)}
                    className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest rounded-md transition-all ${
                      hydrPeriod === p.key
                        ? 'bg-blue-500/25 text-blue-200 shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`hydration-period-${p.key}`}
                  >
                    {language === 'fr' ? p.fr : p.en}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className={hydrPeriod === 'year' ? 'h-56' : 'h-48'}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={hydrChartData}>
                  <defs>
                    <linearGradient id="colorWater" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="rgba(255,255,255,0.5)"
                    fontSize={10}
                    interval={hydrPeriod === 'week' ? 0 : 'preserveStartEnd'}
                    minTickGap={hydrPeriod === 'year' ? 30 : 12}
                    tickFormatter={(value) => {
                      if (!value) return '';
                      // Year view: value is already a 3-letter month
                      if (hydrPeriod === 'year' && value.length <= 4) return value;
                      const date = new Date(value);
                      if (isNaN(date)) return value;
                      if (hydrPeriod === 'week') {
                        return date.toLocaleDateString(t('hydration.fr_fr', 'fr-FR'), { weekday: 'short' });
                      }
                      return date.toLocaleDateString(t('hydration.fr_fr', 'fr-FR'), { day: '2-digit', month: 'short' });
                    }}
                  />
                  <YAxis 
                    stroke="rgba(255,255,255,0.5)"
                    fontSize={10}
                    tickFormatter={(value) => `${(value/1000).toFixed(1)}L`}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'rgba(0,0,0,0.9)',
                      border: '1px solid rgba(59,130,246,0.5)',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [`${value} ml`, t('hydration.consomme', 'Consommé')]}
                    labelFormatter={(label) => {
                      if (hydrPeriod === 'year') return label; // already Month short
                      const d = new Date(label);
                      return isNaN(d) ? label : d.toLocaleDateString(t('hydration.fr_fr', 'fr-FR'));
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="total_ml" 
                    stroke="#3b82f6" 
                    fillOpacity={1} 
                    fill="url(#colorWater)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-500">{stats.average_ml || 0}</p>
                <p className="text-xs text-muted-foreground">{t('hydration.moyenne_ml', 'Moyenne (ml)')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-500">{stats.days_goal_reached || 0}</p>
                <p className="text-xs text-muted-foreground">{t('hydration.objectifs_atteints', 'Objectifs atteints')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">{stats.success_rate || 0}%</p>
                <p className="text-xs text-muted-foreground">{t('hydration.taux_de_reussite', 'Taux de réussite')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Recommendation Card */}
        {recommendation && (
          <Card className="bg-secondary/50 border-border">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                {t('hydration.votre_objectif_personnalise', 'Votre Objectif Personnalisé')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-500/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-black text-blue-500">{recommendation.daily_liters}L</p>
                  <p className="text-xs text-muted-foreground">{t('hydration.par_jour', 'par jour')}</p>
                </div>
                <div className="bg-cyan-500/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-black text-cyan-500">{recommendation.glasses_250ml}</p>
                  <p className="text-xs text-muted-foreground">{t('hydration.verres_de_250ml', 'verres de 250ml')}</p>
                </div>
              </div>
              
              {recommendation.age_note && (
                <div className="flex items-start gap-2 bg-yellow-500/10 rounded-lg p-3">
                  <Info className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-yellow-500">{recommendation.age_note}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Tips Card */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              {t('hydration.conseils_hydratation', 'Conseils Hydratation')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Sun className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                {t('hydration.buvez_un_verre_d_eau_des_le_reveil', 'Buvez un verre d\\\'eau dès le réveil')}
              </li>
              <li className="flex items-start gap-2">
                <GlassWater className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                {t('hydration.gardez_une_bouteille_d_eau_visible', 'Gardez une bouteille d\\\'eau visible')}
              </li>
              <li className="flex items-start gap-2">
                <TrendingUp className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                {t('hydration.buvez_avant_pendant_et_apres_l_exercice', 'Buvez avant, pendant et après l\\\'exercice')}
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
      
      {/* Calculator Modal */}
      {showCalculator && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="font-heading text-xl uppercase flex items-center gap-2">
                <Calculator className="w-6 h-6 text-primary" />
                {t('hydration.calculateur_d_eau', 'Calculateur d\\\'eau')}
              </CardTitle>
              <CardDescription>
                {t('hydration.calculez_votre_besoin_quotidien_en_eau_b', 'Calculez votre besoin quotidien en eau basé sur votre profil')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('hydration.poids_kg', 'Poids (kg)')}</Label>
                <Input
                  type="number"
                  value={calcData.weight_kg}
                  onChange={(e) => setCalcData({...calcData, weight_kg: e.target.value})}
                  placeholder="70"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('hydration.age', 'Âge')}</Label>
                <Input
                  type="number"
                  value={calcData.age}
                  onChange={(e) => setCalcData({...calcData, age: e.target.value})}
                  placeholder="30"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('hydration.sexe', 'Sexe')}</Label>
                <Select value={calcData.sex} onValueChange={(v) => setCalcData({...calcData, sex: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t('hydration.homme', 'Homme')}</SelectItem>
                    <SelectItem value="female">{t('hydration.femme', 'Femme')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{t('hydration.niveau_d_activite', 'Niveau d\\\'activité')}</Label>
                <Select value={calcData.activity_level} onValueChange={(v) => setCalcData({...calcData, activity_level: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sedentary">{t('hydration.sedentaire', 'Sédentaire')}</SelectItem>
                    <SelectItem value="light">{t('hydration.leger', 'Léger')}</SelectItem>
                    <SelectItem value="moderate">{t('hydration.modere', 'Modéré')}</SelectItem>
                    <SelectItem value="active">{t('hydration.actif', 'Actif')}</SelectItem>
                    <SelectItem value="very_active">{t('hydration.tres_actif', 'Très actif')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{t('hydration.climat', 'Climat')}</Label>
                <Select value={calcData.climate} onValueChange={(v) => setCalcData({...calcData, climate: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cold">{t('hydration.froid', 'Froid')}</SelectItem>
                    <SelectItem value="temperate">{t('hydration.tempere', 'Tempéré')}</SelectItem>
                    <SelectItem value="hot">{t('hydration.chaud', 'Chaud')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowCalculator(false)}>
                  {t('hydration.annuler', 'Annuler')}
                </Button>
                <Button 
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  onClick={calculateWaterNeeds}
                  disabled={calculating}
                >
                  {calculating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
                  {t('hydration.calculer', 'Calculer')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Reminders Modal */}
      {showReminders && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="font-heading text-xl uppercase flex items-center gap-2">
                <Bell className="w-6 h-6 text-primary" />
                {t('hydration.rappels_d_eau', 'Rappels d\\\'eau')}
              </CardTitle>
              <CardDescription>
                {t('hydration.configurez_vos_rappels_pour_boire_de_l_e', 'Configurez vos rappels pour boire de l\\\'eau')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('hydration.activer_les_rappels', 'Activer les rappels')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('hydration.recevoir_des_notifications', 'Recevoir des notifications')}
                  </p>
                </div>
                <Switch
                  checked={reminderSettings.enabled}
                  onCheckedChange={(checked) => setReminderSettings({...reminderSettings, enabled: checked})}
                />
              </div>
              
              {reminderSettings.enabled && (
                <>
                  {/* Time range */}
                  <div className="space-y-3">
                    <Label>{t('hydration.plage_horaire', 'Plage horaire')}</Label>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Sun className="w-4 h-4 text-yellow-500" />
                        <Select 
                          value={reminderSettings.start_hour.toString()} 
                          onValueChange={(v) => setReminderSettings({...reminderSettings, start_hour: parseInt(v)})}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({length: 24}, (_, i) => (
                              <SelectItem key={i} value={i.toString()}>{i}h</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <span className="text-muted-foreground">-</span>
                      <div className="flex items-center gap-2">
                        <Moon className="w-4 h-4 text-blue-500" />
                        <Select 
                          value={reminderSettings.end_hour.toString()} 
                          onValueChange={(v) => setReminderSettings({...reminderSettings, end_hour: parseInt(v)})}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({length: 24}, (_, i) => (
                              <SelectItem key={i} value={i.toString()}>{i}h</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  
                  {/* Interval */}
                  <div className="space-y-3">
                    <Label>{t('hydration.intervalle_entre_rappels', 'Intervalle entre rappels')}</Label>
                    <Select 
                      value={reminderSettings.interval_minutes.toString()} 
                      onValueChange={(v) => setReminderSettings({...reminderSettings, interval_minutes: parseInt(v)})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 {t('hydration.minutes', 'minutes')}</SelectItem>
                        <SelectItem value="45">45 {t('hydration.minutes', 'minutes')}</SelectItem>
                        <SelectItem value="60">1 {t('hydration.heure', 'heure')}</SelectItem>
                        <SelectItem value="90">1h30</SelectItem>
                        <SelectItem value="120">2 {t('hydration.heures', 'heures')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Sound */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {reminderSettings.sound_enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                      <span>{t('hydration.son_de_notification', 'Son de notification')}</span>
                    </div>
                    <Switch
                      checked={reminderSettings.sound_enabled}
                      onCheckedChange={(checked) => setReminderSettings({...reminderSettings, sound_enabled: checked})}
                    />
                  </div>
                </>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowReminders(false)}>
                  {t('hydration.annuler', 'Annuler')}
                </Button>
                <Button 
                  className="flex-1"
                  onClick={saveReminderSettings}
                  disabled={savingReminders}
                >
                  {savingReminders ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                  {t('hydration.enregistrer', 'Enregistrer')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Target Editor Modal */}
      {showTargetEditor && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="font-heading text-xl uppercase flex items-center gap-2">
                <Target className="w-6 h-6 text-primary" />
                {t('hydration.modifier_l_objectif', 'Modifier l\\\'objectif')}
              </CardTitle>
              <CardDescription>
                {t('hydration.definissez_votre_objectif_d_hydratation_', 'Définissez votre objectif d\\\'hydratation quotidien')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Quick presets */}
              <div className="space-y-2">
                <Label>{t('hydration.prereglages_rapides', 'Préréglages rapides')}</Label>
                <div className="grid grid-cols-4 gap-2">
                  {[1500, 2000, 2500, 3000].map(ml => (
                    <Button
                      key={ml}
                      variant={customTarget === ml ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCustomTarget(ml)}
                    >
                      {ml / 1000}L
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Custom value */}
              <div className="space-y-2">
                <Label>{t('hydration.objectif_personnalise_ml', 'Objectif personnalisé (ml)')}</Label>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCustomTarget(Math.max(1000, customTarget - 250))}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    min="1000"
                    max="6000"
                    step="250"
                    value={customTarget}
                    onChange={(e) => setCustomTarget(parseInt(e.target.value) || 2000)}
                    className="text-center text-lg font-bold"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCustomTarget(Math.min(6000, customTarget + 250))}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-center text-2xl font-black text-blue-500">
                  {(customTarget / 1000).toFixed(2)}L
                </p>
                <p className="text-center text-sm text-muted-foreground">
                  = {Math.round(customTarget / 250)} {t('hydration.verres_de_250ml', 'verres de 250ml')}
                </p>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowTargetEditor(false)}>
                  {t('hydration.annuler', 'Annuler')}
                </Button>
                <Button 
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  onClick={saveCustomTarget}
                  disabled={savingTarget}
                >
                  {savingTarget ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  {t('hydration.enregistrer', 'Enregistrer')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Weekly Summary Modal */}
      {showWeeklySummary && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="font-heading text-xl uppercase flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-primary" />
                {t('hydration.resume_hebdomadaire', 'Résumé hebdomadaire')}
              </CardTitle>
              {weeklySummary && (
                <CardDescription>
                  {weeklySummary.week_start} → {weeklySummary.week_end}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {loadingWeekly ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : weeklySummary ? (
                <div className="space-y-6">
                  {/* Performance Badge */}
                  <div className={`text-center p-6 rounded-xl ${
                    weeklySummary.performance.level === 'excellent' ? 'bg-green-500/20' :
                    weeklySummary.performance.level === 'good' ? 'bg-blue-500/20' :
                    weeklySummary.performance.level === 'moderate' ? 'bg-yellow-500/20' :
                    'bg-red-500/20'
                  }`}>
                    <span className="text-5xl">{weeklySummary.performance.emoji}</span>
                    <p className="font-heading text-lg mt-2 uppercase">
                      {weeklySummary.performance.level === 'excellent' ? (t('hydration.excellent', 'Excellent!')) :
                       weeklySummary.performance.level === 'good' ? (t('hydration.bien_joue', 'Bien joué!')) :
                       weeklySummary.performance.level === 'moderate' ? (t('hydration.peut_mieux_faire', 'Peut mieux faire')) :
                       (t('hydration.a_ameliorer', 'À améliorer'))}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{weeklySummary.performance.message}</p>
                  </div>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-blue-500/10 rounded-lg p-4 text-center">
                      <p className="text-2xl font-black text-blue-500">
                        {(weeklySummary.summary.average_daily_ml / 1000).toFixed(1)}L
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('hydration.moyenne_jour', 'Moyenne/jour')}
                      </p>
                    </div>
                    <div className="bg-green-500/10 rounded-lg p-4 text-center">
                      <p className="text-2xl font-black text-green-500">
                        {weeklySummary.summary.success_rate}%
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('hydration.taux_de_reussite', 'Taux de réussite')}
                      </p>
                    </div>
                    <div className="bg-purple-500/10 rounded-lg p-4 text-center">
                      <p className="text-2xl font-black text-purple-500">
                        {weeklySummary.summary.days_goal_reached}/7
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('hydration.objectifs_atteints', 'Objectifs atteints')}
                      </p>
                    </div>
                    <div className="bg-cyan-500/10 rounded-lg p-4 text-center">
                      <p className="text-2xl font-black text-cyan-500">
                        {(weeklySummary.summary.total_intake_ml / 1000).toFixed(1)}L
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('hydration.total_semaine', 'Total semaine')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Best/Worst Days */}
                  {(weeklySummary.summary.best_day?.date || weeklySummary.summary.worst_day?.date) && (
                    <div className="grid grid-cols-2 gap-3">
                      {weeklySummary.summary.best_day?.date && (
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Trophy className="w-4 h-4 text-green-500" />
                            <span className="text-xs font-medium text-green-500">
                              {t('hydration.meilleur_jour', 'Meilleur jour')}
                            </span>
                          </div>
                          <p className="text-lg font-bold">{(weeklySummary.summary.best_day.ml / 1000).toFixed(1)}L</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(weeklySummary.summary.best_day.date).toLocaleDateString(t('hydration.fr_fr', 'fr-FR'), { weekday: 'long' })}
                          </p>
                        </div>
                      )}
                      {weeklySummary.summary.worst_day?.date && weeklySummary.summary.worst_day.ml > 0 && (
                        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Frown className="w-4 h-4 text-orange-500" />
                            <span className="text-xs font-medium text-orange-500">
                              {t('hydration.jour_le_plus_faible', 'Jour le plus faible')}
                            </span>
                          </div>
                          <p className="text-lg font-bold">{(weeklySummary.summary.worst_day.ml / 1000).toFixed(1)}L</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(weeklySummary.summary.worst_day.date).toLocaleDateString(t('hydration.fr_fr', 'fr-FR'), { weekday: 'long' })}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Daily breakdown */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium">{t('hydration.detail_par_jour', 'Détail par jour')}</p>
                    {weeklySummary.daily_data.map((day, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground w-10">
                          {new Date(day.date).toLocaleDateString(t('hydration.fr_fr', 'fr-FR'), { weekday: 'short' })}
                        </span>
                        <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${day.goal_reached ? 'bg-green-500' : 'bg-blue-500'}`}
                            style={{ width: `${Math.min(day.percentage, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium w-12 text-right">
                          {day.ml > 0 ? `${(day.ml / 1000).toFixed(1)}L` : '-'}
                        </span>
                        {day.goal_reached && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                      </div>
                    ))}
                  </div>
                  
                  {/* Tips */}
                  {weeklySummary.tips.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">{t('hydration.conseils', 'Conseils')}</p>
                      {weeklySummary.tips.map((tip, i) => (
                        <div key={i} className="flex items-start gap-2 p-2 bg-secondary/50 rounded-lg">
                          <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          <p className="text-sm">{tip.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  {t('hydration.aucune_donnee_disponible', 'Aucune donnée disponible')}
                </p>
              )}
              
              <div className="flex gap-2 mt-6">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => setShowWeeklySummary(false)}
                >
                  {t('hydration.fermer', 'Fermer')}
                </Button>
                <Button 
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  onClick={sendEmailReport}
                  disabled={sendingEmail}
                >
                  {sendingEmail ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  {t('hydration.recevoir_par_email', 'Recevoir par email')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
};

export default Hydration;
